import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { CORP_STATUSES } from "./shared";

export function CorporateTab({ corporate, updateCorpStatus, readOnly }) {
  return (
    <div className="rounded-lg border border-white/10 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-muted-foreground">
              <th className="p-4 font-medium">Company</th>
              <th className="p-4 font-medium">Contact</th>
              <th className="p-4 font-medium hidden md:table-cell">Team / Trips</th>
              <th className="p-4 font-medium hidden lg:table-cell">Received</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody data-testid="corporate-leads-table">
            {corporate.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">No corporate inquiries yet.</td></tr>
            )}
            {corporate.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`corp-lead-${c.id}`}>
                <td className="p-4 text-white font-medium">{c.company_name}</td>
                <td className="p-4">
                  <div className="text-white">{c.contact_name}</div>
                  <a href={`mailto:${c.email}`} className="text-muted-foreground text-xs hover:text-white">{c.email}</a>
                  {c.phone && <div className="text-muted-foreground text-xs">{c.phone}</div>}
                  {c.message && <div className="text-white/50 text-xs mt-1 max-w-xs truncate" title={c.message}>{c.message}</div>}
                </td>
                <td className="p-4 text-muted-foreground hidden md:table-cell">{c.employees || "—"}</td>
                <td className="p-4 text-muted-foreground hidden lg:table-cell">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <Select value={c.status} onValueChange={(v) => updateCorpStatus(c.id, v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`corp-status-${c.id}`} className="h-9 w-36 bg-secondary border-white/10 text-white capitalize disabled:opacity-60">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CORP_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RequestsTab({ requests, resolveRequest, readOnly }) {
  return (
    <div className="rounded-lg border border-white/10 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-muted-foreground">
              <th className="p-4 font-medium">Type</th>
              <th className="p-4 font-medium">Reservation</th>
              <th className="p-4 font-medium">Customer</th>
              <th className="p-4 font-medium hidden md:table-cell">Message</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody data-testid="requests-table">
            {requests.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">No modification or cancellation requests yet.</td></tr>
            )}
            {requests.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`request-${r.id}`}>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border ${r.type === "cancellation" ? "border-white/40 text-white" : "border-white/20 text-white/70"}`}>{r.type}</span>
                </td>
                <td className="p-4 text-white">{r.reservation_number || r.booking_id}</td>
                <td className="p-4"><a href={`mailto:${r.email}`} className="text-muted-foreground hover:text-white">{r.email}</a></td>
                <td className="p-4 text-white/60 hidden md:table-cell max-w-xs truncate" title={r.message}>{r.message || "—"}</td>
                <td className="p-4">
                  <button data-testid={`request-toggle-${r.id}`} disabled={readOnly}
                    onClick={() => resolveRequest(r.id, r.status === "open" ? "resolved" : "open")}
                    className={`text-[10px] uppercase tracking-luxe px-3 py-1.5 rounded-full border disabled:opacity-60 disabled:cursor-default ${r.status === "resolved" ? "border-white/15 text-muted-foreground" : "border-white/40 text-white bg-white/10"}`}>
                    {r.status === "resolved" ? "Resolved" : "Mark resolved"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AutomationsTab({ autoLog, runAutomations, runningAuto, canRun }) {
  return (
    <>
      <div className="rounded-lg border border-white/10 bg-card p-6 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-white text-lg mb-1">Email automations</h3>
          <p className="text-muted-foreground text-sm">Quote follow-ups sent: {autoLog.counts?.quote_followup ?? 0} · Balance reminders sent: {autoLog.counts?.balance_reminder ?? 0} · Driver trip sheets sent: {autoLog.counts?.driver_trip_reminder ?? 0}. Runs automatically every 30 minutes.</p>
        </div>
        {canRun && (
          <Button data-testid="run-automations-btn" onClick={runAutomations} disabled={runningAuto}
            className="rounded-full bg-white text-black hover:bg-white/85 px-7">
            {runningAuto ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run now"}
          </Button>
        )}
      </div>
      <div className="rounded-lg border border-white/10 bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-muted-foreground">
                <th className="p-4 font-medium">Type</th>
                <th className="p-4 font-medium">Reservation</th>
                <th className="p-4 font-medium hidden md:table-cell">Customer</th>
                <th className="p-4 font-medium">Sent</th>
              </tr>
            </thead>
            <tbody data-testid="automations-log-table">
              {(autoLog.logs || []).length === 0 && (
                <tr><td colSpan="4" className="p-8 text-center text-muted-foreground">No automated emails sent yet.</td></tr>
              )}
              {(autoLog.logs || []).map((l, i) => (
                <tr key={l.id || `${l.booking_id}-${l.kind}-${i}`} className="border-b border-white/5">
                  <td className="p-4 text-white/80">{l.kind === "quote_followup" ? "Quote follow-up" : l.kind === "driver_trip_reminder" ? "Driver trip sheet" : "Balance reminder"}</td>
                  <td className="p-4 text-white">{l.reservation_number || l.booking_id}</td>
                  <td className="p-4 text-muted-foreground hidden md:table-cell">{l.customer || "—"}</td>
                  <td className="p-4 text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
